/**
 * Whether a file's bytes are something this application can show in place
 * (FIL-8a, FIL-8b).
 *
 * **Decided from the name, never from `FileSummary.kind`**, and that is the
 * whole reason this module exists. The API derives `kind` in
 * `apps/api/src/shared/fileKind.ts`, where `tif`, `tiff`, `heic` and `heif` all
 * map to `PHOTOS` beside `jpg` and `png` — and no mainstream browser decodes
 * any of the first four. A viewer keyed on the column would open an empty frame
 * for exactly the formats a phone extraction is full of, and would do it *after*
 * spending an audited, metered download on the bytes (UPL-8).
 *
 * `kind` also carries `PDF`, which makes the shortcut look safe for FIL-8b in a
 * way it never was for FIL-8a. It is not: `kind` is derived from the content
 * type when the extension is unknown, so a `.bin` uploaded as `application/pdf`
 * is `PDF` on the wire. The extension is the gate for both formats, for one
 * reason.
 *
 * This does not cross FIL-4's rule that `kind` is informational and never
 * behavioral. Nothing here reads `kind`; the question being answered — can this
 * application show these bytes — is answered inside the web app and changes
 * nothing about how the file is stored, hashed, scanned, or authorized.
 */

/**
 * The formats every browser this product supports decodes natively.
 *
 * **`svg` is absent deliberately and must stay absent.** An SVG is a script
 * container: painting one from an object URL executes it in this application's
 * own origin, which is cross-site scripting delivered as evidence. It downloads
 * like any other file, which is the correct handling for it.
 *
 * `heic`, `heif`, `tif` and `tiff` are absent for the reason in the header —
 * the API calls them `PHOTOS`, and that is not the same question. Making them
 * viewable means shipping a WASM decoder, which is a dependency, an ADR, and a
 * separate decision.
 */
const VIEWABLE_IMAGES = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'bmp']);

/**
 * One entry, and it is the whole of FIL-8b's gate.
 *
 * A set of one rather than a string comparison so that the two predicates read
 * the same and `extensionOf` stays the single place the parsing rules live —
 * the dotfile case and the last-extension-wins case are easy to get subtly
 * different if they are written twice.
 */
const VIEWABLE_DOCUMENTS = new Set(['pdf']);

/**
 * The last extension, lowercased, or an empty string when there is not one.
 *
 * `< 1` rather than `< 0`: a name that *starts* with the dot has no base, so a
 * file called `.jpg` is a dotfile and not a photograph.
 */
function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  if (dot < 1) return '';

  return fileName.slice(dot + 1).toLowerCase();
}

export function isViewableImage(fileName: string): boolean {
  return VIEWABLE_IMAGES.has(extensionOf(fileName));
}

export function isViewablePdf(fileName: string): boolean {
  return VIEWABLE_DOCUMENTS.has(extensionOf(fileName));
}

/**
 * Whether the file's name opens something rather than downloading it.
 *
 * The predicate the folder table asks. It exists so that the three call sites
 * which only care *whether* a row is viewable — the name's behaviour, whether
 * the row needs an overflow menu, and what goes in it — do not each grow a
 * parallel `|| isViewablePdf(...)` that a fourth format would have to find.
 * Which viewer to open is a separate question, asked once.
 */
export function isViewable(fileName: string): boolean {
  return isViewableImage(fileName) || isViewablePdf(fileName);
}
